import { ChangeDetectionStrategy, Component, OnDestroy, ViewChild, ViewContainerRef, computed, effect, inject, input, output, untracked } from '@angular/core';
import { clpp } from '@castlabs/prestoplay';

import { TranslocoPipe } from '@jsverse/transloco';
import { ContentRatingGate } from '@core/content-ratings';
import { SkinningService } from '@core/skin';
import { BadgeComponent } from 'app/shared/components/badge/badge.component';
import { BadgeVariant } from 'app/shared/components/badge/badge.model';
import { ContentRatingLock } from '@shared/components/content-rating-lock/content-rating-lock';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';

import { IDetailsViewModel } from '@features/details/data-access/details.models';
import { toPlayerItem } from '@features/details/utils/details-player.mapper';
import { PlayerService } from '@features/player/data-access/player.service';
import { PlayerTypes } from '@features/player/data-access/player.config';
import { PlayerItem } from '@features/player/data-access/player.model';

/** Shared header: backdrop poster, channel icon, badge, title and the meta row. */
@Component({
    selector: 'details-header',
    standalone: true,
    imports: [BadgeComponent, ContentRatingLock, TranscodeImageDirective, RoundedIconButton, TranslocoPipe],
    templateUrl: './details-header.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetailsHeader implements OnDestroy {
    readonly vm = input.required<IDetailsViewModel>();
    readonly channelIcon = input<string | null>(null);
    /** Resolved trailer/promoted-extra URL for the auto-preview (null = no preview). */
    readonly previewUrl = input<string | null>(null);
    readonly showPlay = input(false);

    readonly play = output<void>();

    protected readonly skinning = inject(SkinningService);
    protected readonly BadgeVariant = BadgeVariant;
    private readonly gate = inject(ContentRatingGate);
    /** Above the active profile's content rating: backdrop blurred behind a padlock (AVIION-36952). */
    protected readonly restricted = computed(() => this.gate.isRestricted(this.vm()));

    private readonly playerService = inject(PlayerService);

    /** The shared player view is relocated into this anchor while a trailer preview plays. */
    @ViewChild('playerContainer', { read: ViewContainerRef, static: false }) playerContainer!: ViewContainerRef;

    /** True while the trailer preview is the active playback in our `Details` layout slot. */
    protected readonly previewVideoPlaying = computed(() =>
        this.playerService.getState()() === clpp.Player.State.PLAYING
        && this.playerService.getPlayerLayoutType()() === PlayerTypes.Details);
    protected readonly previewVideoMuted = this.playerService.isPlayerMuted();

    private previewTimeout: any;

    constructor() {
        // Auto-start the trailer preview shortly after the header (re)renders for an item that has one.
        // The header is recreated per open (the store flips `loading` on every `loadByHash`), so this
        // fires once per opened item.
        effect(() => {
            const hasPreview = !!this.previewUrl();
            untracked(() => {
                clearTimeout(this.previewTimeout);
                if (!hasPreview) return;
                // Let the dialog + header settle and the ViewChild bind before relocating the player.
                this.previewTimeout = setTimeout(() => this.handlePreviewVideo(), 1000);
            });
        });
    }

    private handlePreviewVideo(): void {
        const previewUrl = this.previewUrl();
        const vm = this.vm();
        if (this.previewVideoPlaying() || !previewUrl) return;
        if (this.playerService.isPlayerPip()()) return;
        if (!this.playerService.isPreviewAutoplayEnabled()) return;
        // Real playback already started (e.g. Play pressed right after opening details): never start a
        // trailer over it — this timer can fire ~1s after the click.
        if (this.playerService.isPreviewSuppressed()) return;

        const layout = this.playerService.getPlayerLayoutType()();
        if (layout === PlayerTypes.None || layout === PlayerTypes.Details) {
            this.playerService.changePlayerViewLocation(this.playerContainer);
            // The player reads the preview from `previewVideoUrl` (trailer or promoted extra, resolved upstream).
            const playerItem = { ...toPlayerItem(vm), previewVideoUrl: previewUrl } as PlayerItem;
            this.playerService.playItem(playerItem, PlayerTypes.Details);
        } else if (layout === PlayerTypes.Cover) {
            // Hand the already-playing hero/cover trailer off into the details container instead of
            // restarting it (ported from the rc "Fix trailer present" change, f0121915).
            this.playerService.changePlayerViewLocation(this.playerContainer);
            this.playerService.setPlayerLayoutType(PlayerTypes.Details);
        }
    }

    protected toggleMute(): void {
        this.playerService.toggleMute();
    }

    ngOnDestroy(): void {
        // The player is returned to its persistent host upstream (DetailsService.releasePreview, called
        // from the shell BEFORE this view is torn down). Doing it here would be too late — the embedded
        // view is already destroyed by now — so we only clear our own timer.
        clearTimeout(this.previewTimeout);
    }
}
